import { useMemo, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useShallow } from 'zustand/react/shallow';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  Loader2,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import { useNodeStore } from '@/stores/node-store';
import type { NodeStruct } from '@/types/node-types';
import { ConnectionType } from '@/types/edge-types';
import type { NodeStatusEvent } from '@/types/workflow-types';
import {
  getRepeatPseudoCycleConfig,
  REPEAT_DEFAULT_MAX_ITERATIONS,
  REPEAT_FEEDBACK_HANDLE_PREFIX,
  REPEAT_SOURCE_HANDLE,
} from '@/pages/workflow/helper/repeat-control';
import { readLoopRun } from '@/pages/workflow/helper/repeat-run';
import { SlotCard } from './slot-card';
import { cn } from '@/lib/utils';

interface RepeatUntilRowsProps {
  nodeId: string;
  struct: NodeStruct;
  result: unknown;
  nodeStatusEvents?: NodeStatusEvent[];
  isLocked: boolean;
}

/** The outputs every loop needs to see on the canvas; the rest fold away. */
const PRIMARY_OUTPUTS = new Set(['final_value']);

/**
 * The Repeat Until node body, kept to a few rows: the result output, the
 * "repeat" handle that closes the loop, one status chip, and a disclosure
 * for the six bookkeeping outputs (still connectable; opened automatically
 * when one of them is wired).
 */
export function RepeatUntilRows({
  nodeId,
  struct,
  result,
  nodeStatusEvents,
  isLocked,
}: RepeatUntilRowsProps) {
  const { connectedKey, activeConnect } = useNodeStore(
    useShallow((state) => ({
      connectedKey: state.edges
        .filter(
          (edge) =>
            edge.source === nodeId && edge.edge_type !== ConnectionType.REPEAT,
        )
        .map((edge) => edge.sourceHandle)
        .join('|'),
      activeConnect: state.activeConnect,
    })),
  );
  const connectedOutputs = useMemo(
    () => new Set(connectedKey ? connectedKey.split('|') : []),
    [connectedKey],
  );
  const primary = struct.output_ports.filter((port) =>
    PRIMARY_OUTPUTS.has(port.name),
  );
  const secondary = struct.output_ports.filter(
    (port) => !PRIMARY_OUTPUTS.has(port.name),
  );
  const secondaryConnected = secondary.some((port) =>
    connectedOutputs.has(port.id),
  );
  const [moreOpen, setMoreOpen] = useState(false);
  const showMore = moreOpen || secondaryConnected;

  const config = getRepeatPseudoCycleConfig(struct);
  const maxIterations = config?.max_iterations ?? REPEAT_DEFAULT_MAX_ITERATIONS;
  const run = useMemo(
    () =>
      readLoopRun({
        result,
        events: nodeStatusEvents,
        maxIterations,
      }),
    [result, nodeStatusEvents, maxIterations],
  );
  const dragging =
    activeConnect?.side === 'source' && activeConnect.nodeId === nodeId;

  return (
    <div className='mt-2 space-y-1' data-testid='repeat-until-rows'>
      {primary.map((port) => (
        <SlotCard
          key={`${nodeId}-${port.id}-output`}
          nodeId={nodeId}
          port={port}
          portDirection='output'
          edgeType={ConnectionType.PORT_TO_PARAM}
          isLocked={isLocked}
        />
      ))}

      {/* The handle that closes the loop. */}
      <div
        className={cn(
          'relative mx-3 flex items-center justify-between rounded-md border border-dashed border-amber-400 bg-amber-50 dark:bg-amber-500/10 px-2 py-1.5 text-[11px] font-semibold text-amber-900 dark:text-amber-100',
          dragging && 'ring-2 ring-amber-300 dark:ring-amber-500/50',
        )}
        data-testid='repeat-handle-row'
      >
        <span className='flex items-center gap-1'>
          <RotateCcw className='size-3' />
          repeat
          <span className='font-normal text-amber-800/80 dark:text-amber-200/80'>
            → drag to the step, or pick it in the panel
          </span>
        </span>
        <Handle
          id={REPEAT_SOURCE_HANDLE}
          type='source'
          position={Position.Right}
          isConnectable={!isLocked}
          title='Repeat: drag back to the step that should run again'
          data-testid='repeat-source-handle'
          className='!right-[-9px] !size-4 !border-2 !border-white !bg-amber-500'
          isValidConnection={(connection) =>
            Boolean(
              connection.targetHandle?.startsWith(
                REPEAT_FEEDBACK_HANDLE_PREFIX,
              ),
            )
          }
        />
      </div>

      {/* Status chip */}
      <div
        className={cn(
          'mx-3 flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px]',
          run.outcome.tone === 'success' &&
            'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-900 dark:text-emerald-100',
          run.outcome.tone === 'warning' &&
            'bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-100',
          run.outcome.tone === 'error' &&
            'bg-red-50 dark:bg-red-500/10 text-red-900 dark:text-red-100',
          run.outcome.tone === 'running' &&
            'bg-sky-50 dark:bg-sky-500/10 text-sky-900 dark:text-sky-100',
          run.outcome.tone === 'idle' && 'bg-muted/40 text-muted-foreground',
        )}
        data-testid='repeat-status-chip'
      >
        {run.outcome.tone === 'running' ? (
          <Loader2 className='size-3 animate-spin' />
        ) : run.outcome.tone === 'success' ? (
          <CheckCircle2 className='size-3' />
        ) : run.outcome.tone === 'error' ? (
          <XCircle className='size-3' />
        ) : (
          <CircleDashed className='size-3' />
        )}
        <span className='truncate'>
          {run.outcome.tone === 'idle'
            ? `up to ${maxIterations} attempts`
            : run.outcome.tone === 'running'
              ? `attempt ${Math.min(run.outcome.iteration + 1, run.outcome.maxIterations)} of ${run.outcome.maxIterations}`
              : run.outcome.sentence}
        </span>
      </div>

      {/* Bookkeeping outputs */}
      {secondary.length > 0 && (
        <div>
          <button
            type='button'
            className='mx-3 flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground'
            data-testid='repeat-more-outputs'
            onClick={() => setMoreOpen((value) => !value)}
          >
            {showMore ? (
              <ChevronDown className='size-3' />
            ) : (
              <ChevronRight className='size-3' />
            )}
            {showMore ? 'Fewer outputs' : `More outputs (${secondary.length})`}
          </button>
          {showMore && (
            <div className='mt-1 space-y-1'>
              {secondary.map((port) => (
                <SlotCard
                  key={`${nodeId}-${port.id}-output`}
                  nodeId={nodeId}
                  port={port}
                  portDirection='output'
                  edgeType={ConnectionType.PORT_TO_PARAM}
                  isLocked={isLocked}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
